import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import service from '../services/systemAnnouncementService';
import { SystemAnnouncementState } from '../utils/systemAnnouncementState';
import { getTokenSessionId } from '../utils/authUtils';
import { usePreferences } from './PreferenceContext';

const Context = createContext(null);
export const announcementSessionKey = token => token ? getTokenSessionId(token) || token : null;
export const resolveAnnouncementCarrier = (currentEntity, entities) => {
  const candidates = entities.filter(row => row.status === 'active' && /^[1-9]\d*$/.test(String(row.entityId)));
  const id = currentEntity?.entityId ?? currentEntity?.id;
  // Catalogue rows come from successful bootstrap; personal preference-edit
  // capability is unrelated to reading a global announcement through a carrier.
  if (String(id).toLowerCase() === 'all') return candidates[0]?.entityId || null;
  return candidates.find(row => String(row.entityId) === String(id))?.entityId || null;
};
export const resolveAnnouncementScope = (currentEntity, entities) => {
  const carrier = resolveAnnouncementCarrier(currentEntity, entities);
  if (!carrier) return null;
  const type = String(currentEntity?.entityId ?? currentEntity?.id).toLowerCase() === 'all' ? 'global' : 'entity';
  return { type, carrier: Number(carrier), entityId: type === 'entity' ? Number(carrier) : null };
};

export function SystemAnnouncementsProvider({ sessionKey, currentEntity, entities, children }) {
  const location = useLocation();
  const { entitySettings } = usePreferences();
  const resolved = resolveAnnouncementScope(currentEntity, entities);
  const scopeKey = resolved ? `${resolved.type}:${resolved.carrier}` : 'none';
  const owner = `${sessionKey || ''}:${scopeKey}`;
  const ownerRef = useRef(owner); ownerRef.current = owner;
  // Preserve an unknown setting until a boolean value is available.
  const settingEnabled = entitySettings?.entity_announcements_enabled;
  // Replace the store during render, exposing an empty snapshot immediately.
  // The owner ref also fences old promises before layout/passive cleanup runs.
  const store = useMemo(() => new SystemAnnouncementState(service, {
    scope: resolved, settingEnabled,
    isSessionCurrent: () => ownerRef.current === owner && Boolean(sessionKey)
      && sessionKey === announcementSessionKey(localStorage.getItem('token'))
  // Values are captured for one immutable session/audience owner. Preference
  // changes invalidate it below; entity label changes do not discard content.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [owner]);
  const scope = store.scope;
  const carrier = scope?.carrier;
  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot);

  useLayoutEffect(() => () => store.dispose(), [store]);
  useLayoutEffect(() => { store.settingChanged(settingEnabled); }, [store, settingEnabled]);
  useEffect(() => {
    const changed = event => {
      if (scope?.type === 'entity' && String(event.detail?.id) === String(scope.entityId)) {
        store.settingChanged(event.detail.entity_announcements_enabled);
      }
    };
    window.addEventListener('entitySettingsUpdated', changed);
    return () => window.removeEventListener('entitySettingsUpdated', changed);
  }, [store, scope]);
  useEffect(() => {
    if (sessionKey && document.visibilityState !== 'hidden') void store.refresh();
  }, [store, carrier, sessionKey]);
  useEffect(() => {
    if (document.visibilityState !== 'hidden') void store.refresh();
  }, [store, location.pathname, location.search]);
  useEffect(() => {
    const focus = () => { if (document.visibilityState !== 'hidden') void store.visibleAgain(); };
    const visibility = () => document.visibilityState === 'hidden' ? store.hidden() : focus();
    window.addEventListener('focus', focus); document.addEventListener('visibilitychange', visibility);
    return () => { window.removeEventListener('focus', focus); document.removeEventListener('visibilitychange', visibility); };
  }, [store]);
  const entityName = currentEntity?.entityName || currentEntity?.name;
  const value = useMemo(() => ({ ...snapshot, store, carrier, scope, owner, entityName,
    enabled: Boolean(sessionKey && carrier) }), [snapshot, store, sessionKey, carrier, scope, owner, entityName]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}
export const useSystemAnnouncements = () => useContext(Context);
